/**
 * Which services a location offers, from the booking preview's
 * `scheduleAssignedServiceIds` map.
 *
 * A location with no assigned services (no map entry, or an empty list) is
 * the legacy "all services" setting: the location read that fills the booking
 * picker (`GET /v1/locations/:id`) and quote services both treat it that way,
 * so the service menu must too (#830). Once any service is assigned, the list
 * is authoritative.
 */
export function servicesOfferedAtSchedule<T extends { id: string }>(
  services: T[],
  assignedIds: readonly string[] | undefined,
): T[] {
  if (!assignedIds || assignedIds.length === 0) return services;
  const idSet = new Set(assignedIds);
  return services.filter((service) => idSet.has(service.id));
}

/**
 * The services offered at any of `scheduleIds`, in catalog order. One location
 * that offers all services makes the whole catalog offered; no locations at
 * all offers nothing.
 */
export function servicesOfferedAtSchedules<T extends { id: string }>(
  services: T[],
  scheduleIds: readonly string[],
  assignedIdsBySchedule: Record<string, readonly string[] | undefined>,
): T[] {
  const offeredIds = new Set<string>();
  for (const scheduleId of scheduleIds) {
    for (const service of servicesOfferedAtSchedule(
      services,
      assignedIdsBySchedule[scheduleId],
    )) {
      offeredIds.add(service.id);
    }
  }
  return services.filter((service) => offeredIds.has(service.id));
}
